import type { Delta, Op } from 'quill/core';
import Quill from 'quill/core';
import { useLayoutEffect, useState } from 'react';
import { useQuill } from '../editor/QuillContext';
import isInlineTextInsertion from '../editor/utils/isInlineTextInsertion';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import { useOptimisticItemContentRects } from './useOptimisticItemContentRects';
import collectThreadAndAnnotationIds from './utils/collectThreadAndAnnotationIds';

const POST_FRAGMENT = gql(`
  fragment useHasMagneticWallItems_post on PostFields {
    id
    threads {
      id
    }
    annotations {
      id
    }
  }
`);

const hasThreadsOrAnnotationsInOps = (
  ops: Op[],
  threadIds: Set<string>,
  annotationIds: Set<string>,
) => {
  const result = collectThreadAndAnnotationIds(ops);

  const hasThreads = threadIds.intersection(result.allThreadIds).size > 0;
  const hasAnnotations =
    annotationIds.intersection(result.allAnnotationIds).size > 0;

  return hasThreads || hasAnnotations;
};

const useHasMagneticWallItems = (
  post: DocumentType<typeof POST_FRAGMENT> | undefined,
  skip?: boolean,
) => {
  const { quill } = useQuill();

  const [hasThreadsOrAnnotations, setHasThreadsOrAnnotations] = useState(false);
  const optimisticItemContentRects = useOptimisticItemContentRects();
  const hasOptimisticItemContentRects = optimisticItemContentRects.size > 0;

  useLayoutEffect(() => {
    if (!quill || skip) return undefined;

    const checkAndSetMagneticWallItemState = () => {
      const threadIds = new Set(post?.threads.map((thread) => thread.id));
      const annotationIds = new Set(
        post?.annotations.map((annotation) => annotation.id),
      );

      setHasThreadsOrAnnotations(
        hasThreadsOrAnnotationsInOps(
          quill.getContents().ops,
          threadIds,
          annotationIds,
        ),
      );
    };

    checkAndSetMagneticWallItemState();

    const onTextChange = (delta: Delta) => {
      if (isInlineTextInsertion(delta)) return;
      checkAndSetMagneticWallItemState();
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
    };
  }, [quill, post, skip]);

  return !skip && (hasThreadsOrAnnotations || hasOptimisticItemContentRects);
};

export default useHasMagneticWallItems;
